import React from "react";
import { Box, Checkbox } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import CheckedIcon from "../../svg/CheckedIcon";
import UncheckedIcon from "../../svg/UncheckedIcon";
import { GroupCheckState } from "./keragonSelection";

/**
 * One expandable group in step 2 of the "Sync EHR data" modal — a biomarker
 * (rows = dates) in the Biomarkers view, or a date (rows = biomarkers) in the
 * Labs view. Figma 16753:468272 / 16772:502625, with the row checkboxes the
 * design only drew for the Biomarkers view added to both.
 *
 * Header checkbox drives the whole group and shows an indeterminate state when
 * only some rows are ticked.
 */

const HEADER_CHECKBOX = 20;
const ROW_CHECKBOX = 16;

export type GroupRow = {
  key: string;
  left: React.ReactNode;
  right: React.ReactNode;
  checked: boolean;
};

type Props = {
  title: React.ReactNode;
  meta: React.ReactNode;
  /** Right-aligned header note, e.g. "3/3 new". */
  trailing?: React.ReactNode;
  checkState: GroupCheckState;
  expanded: boolean;
  onToggle: () => void;
  onToggleExpand: () => void;
  columns: [string, string];
  rows: GroupRow[];
  onToggleRow: (key: string) => void;
};

const KeragonGroupCard = ({
  title,
  meta,
  trailing,
  checkState,
  expanded,
  onToggle,
  onToggleExpand,
  columns,
  rows,
  onToggleRow,
}: Props) => {
  const theme: Theme = useTheme();

  // Header checkbox is 20px, row checkboxes 16px, as in the design.
  const renderCheckbox = (
    checked: boolean,
    indeterminate: boolean,
    onChange: () => void,
    size: number
  ) => (
    <Checkbox
      icon={<UncheckedIcon fill={theme.colors_ds.NEUTRAL_ICON_WEAK} size={size} />}
      checkedIcon={<CheckedIcon fill={theme.colors_ds.PRIMARY_ICON_MAIN} size={size} />}
      indeterminateIcon={<CheckedIcon fill={theme.colorTokens.N[60]} size={size} />}
      checked={checked}
      indeterminate={indeterminate}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      disableRipple
      sx={styles.checkbox(size)}
    />
  );

  return (
    <Box sx={styles.card(theme)}>
      <Box sx={styles.header(theme, expanded)} onClick={onToggleExpand}>
        {renderCheckbox(checkState === "all", checkState === "some", onToggle, HEADER_CHECKBOX)}
        <Box sx={styles.titleGroup}>
          <Box sx={styles.title(theme)}>{title}</Box>
          <Box sx={styles.meta(theme)}>{meta}</Box>
        </Box>
        {trailing !== undefined && <Box sx={styles.trailing(theme)}>{trailing}</Box>}
        <KeyboardArrowDownIcon sx={styles.chevron(theme, expanded)} />
      </Box>

      {expanded && (
        <Box sx={styles.body}>
          <Box sx={styles.columnsRow}>
            <Box sx={styles.checkboxSpacer} />
            <Box sx={{ ...styles.columnLabel(theme), flex: 1 }}>{columns[0]}</Box>
            <Box sx={{ ...styles.columnLabel(theme), flex: 1 }}>{columns[1]}</Box>
          </Box>
          {rows.map((row) => (
            <Box key={row.key} sx={styles.row(theme)}>
              {renderCheckbox(row.checked, false, () => onToggleRow(row.key), ROW_CHECKBOX)}
              <Box sx={styles.rowLeft(theme)}>{row.left}</Box>
              <Box sx={styles.rowRight(theme)}>{row.right}</Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default KeragonGroupCard;

const styles = {
  card: (theme: Theme) => ({
    border: `1px solid ${theme.colorTokens.N[50]}`,
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    overflow: "hidden",
    flexShrink: 0,
  }),
  // Figma #F7F9FA has no token; N30 is the nearest neutral tint.
  header: (theme: Theme, expanded: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "12px",
    cursor: "pointer",
    backgroundColor: theme.colorTokens.N[30],
    borderBottom: expanded ? `1px solid ${theme.colorTokens.N[50]}` : "none",
  }),
  checkbox: (size: number) => ({ padding: 0, width: `${size}px`, height: `${size}px` }),
  // Column labels line up with the row text, so the spacer matches a row checkbox.
  checkboxSpacer: { width: `${ROW_CHECKBOX}px`, flexShrink: 0 },
  titleGroup: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
    flex: 1,
    minWidth: 0,
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colorTokens.N[90],
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  meta: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[80],
  }),
  trailing: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    whiteSpace: "nowrap" as "nowrap",
  }),
  chevron: (theme: Theme, expanded: boolean) => ({
    fontSize: "18px",
    color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.15s ease",
    flexShrink: 0,
  }),
  body: {
    display: "flex",
    flexDirection: "column" as "column",
    padding: "0 12px 4px",
  },
  columnsRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "10px 0 6px",
  },
  columnLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "19px",
    color: theme.colorTokens.N[80],
  }),
  row: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    minHeight: "38px",
    padding: "4px 0",
    borderTop: `1px solid ${theme.colorTokens.N[50]}`,
  }),
  rowLeft: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[90],
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  rowRight: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[90],
  }),
};
